// src/utils/programs/dateCalculations.js
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

/**
 * Calculate program duration (days and nights) from date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Object} { durations: number, nights: number }
 */
export const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) {
        return { durations: 0, nights: 0 };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
        return { durations: 0, nights: 0 };
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days

    return {
        durations: diffDays,
        nights: diffDays - 1,
    };
};

/**
 * Check if date range is valid
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {boolean}
 */
export const isValidDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);

    return end >= start;
};

/**
 * Generate array of day objects based on start date and duration
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {number} duration - Number of days
 * @returns {Array} Array of day objects
 */
export const generateDayBoxes = (startDate, duration) => {
    const days = [];
    const start = new Date(startDate);

    for (let i = 0; i < duration; i++) {
        const currentDate = new Date(start);
        currentDate.setDate(start.getDate() + i);

        days.push({
            day_number: i + 1,
            date: currentDate.toISOString().split('T')[0],
            day_name: { ar: '', en: '' },
            products: [],
        });
    }

    return days;
};

/**
 * Format date for display
 * @param {string} dateString - Date string
 * @param {string} locale - Locale parameter (ignored, always uses enUS for standardized display)
 * @returns {string} Formatted date
 */
export const formatDate = (dateString, locale = 'en') => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return format(date, 'dd MMM yyyy', {
            locale: enUS,
        });
    } catch (e) {
        return dateString;
    }
};

/**
 * Format date to YYYY-MM-DD
 * @param {Date|string} date - Date object or string
 * @returns {string} Formatted date string
 */
export const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

/**
 * Get day of week
 * @param {string} dateString - Date string
 * @param {string} locale - Locale ('ar' or 'en')
 * @returns {string} Day of week
 */
export const getDayOfWeek = (dateString, locale = 'en') => {
    if (!dateString) return '';

    const date = new Date(dateString);

    if (locale === 'ar') {
        return date.toLocaleDateString('ar-SA', { weekday: 'long' });
    }

    return date.toLocaleDateString('en-US', { weekday: 'long' });
};

/**
 * Get list of dates between start and end
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {Array<string>} Array of date strings
 */
export const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    const current = new Date(start);
    while (current <= end) {
        dates.push(formatDateToYYYYMMDD(current));
        current.setDate(current.getDate() + 1);
    }

    return dates;
};

/**
 * Check if reducing days will cause data loss
 * @param {Array} currentItinerary - Current itinerary array
 * @param {number} newDurations - New duration (days)
 * @returns {Object} { canChange: boolean, affectedDays: Array }
 */
export const validateDateRangeChange = (currentItinerary, newDurations) => {
    if (newDurations >= currentItinerary.length) {
        return { canChange: true, affectedDays: [] };
    }

    const affectedDays = [];
    for (let i = newDurations; i < currentItinerary.length; i++) {
        if (currentItinerary[i]?.products?.length > 0) {
            affectedDays.push({
                dayNumber: i + 1,
                productCount: currentItinerary[i].products.length,
            });
        }
    }

    return {
        canChange: affectedDays.length === 0,
        affectedDays,
    };
};

/**
 * Add days to a date
 * @param {string} dateString - Date string (YYYY-MM-DD)
 * @param {number} days - Number of days to add
 * @returns {string} New date string
 */
export const addDays = (dateString, days) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    date.setDate(date.getDate() + days);

    return formatDateToYYYYMMDD(date);
};

/**
 * Get difference in days between two dates
 * @param {string} date1 - First date
 * @param {string} date2 - Second date
 * @returns {number} Difference in days
 */
export const getDaysDifference = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    const diffTime = Math.abs(d2 - d1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
